import { DecimalPipe, NgFor, NgIf, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy, ViewChild, ViewEncapsulation, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { fuseAnimations } from '@fuse/animations/public-api';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { ChartComponent } from "ng-apexcharts";
import { DataVisualBarRecentService } from './dv-bar-recent.service';
import { ApexNonAxisChartSeries, ApexResponsive, ApexChart, ApexStroke, ApexFill } from "ng-apexcharts";
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { DateTime } from 'luxon';

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  stroke: ApexStroke;
  fill: ApexFill;
};

@Component({
    selector     : 'dv-bar-recent',
    templateUrl  : './dv-bar-recent.component.html',
    styleUrls      : ['./dv-bar-recent.component.scss'],
    animations: [
        fuseAnimations,
        trigger('slideDown', [
          state('void', style({
            height: '0',
            opacity: '0',
            overflow: 'hidden',
          })),
          state('*', style({
            height: '*',
            opacity: '1',
          })),
          transition(':leave', [
            animate('0.3s ease-out')
          ]),
        ])
      ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone   : true,
    imports      : [
        MatButtonModule, 
        MatIconModule, 
        MatTooltipModule,
        FormsModule, 
        ReactiveFormsModule, 
        MatFormFieldModule, 
        MatInputModule, 
        MatExpansionModule,
        MatProgressBarModule,
        MatDividerModule,
        CommonModule,
        NgFor,
        NgIf,
        FuseScrollbarDirective,
        MatMenuModule, 
        MatButtonToggleModule, 
        NgApexchartsModule, 
        MatTooltipModule, 
        NgFor, 
        DecimalPipe,
    ],
})

export class DataVisualBarRecentComponent implements OnInit, OnChanges {
  
    chartFileCount: ApexOptions = {};
    chartFileDistribution: ApexOptions = {};

    data: any;
    workaroundData: any;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    now = DateTime.now();
    public params: string = 'current';

    /**
     * Constructor
     */
    constructor(
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _formBuilder: UntypedFormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _router: Router,
        private _dataService: DataVisualBarRecentService,
    )
    {
        
    }

    ngOnChanges(changes: SimpleChanges): void {
        
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * Initializes the component.
     * Subscribes to the data service to fetch data and prepares chart data.
     */
    ngOnInit(): void {

        this._dataService.getData().subscribe({
            next: (data) => {
                console.log("Data fetched", data);
                this._prepareChartFileCount()
                this._prepareChartFileDistribution();
            },
            error: (error) => {
                console.error("Error fetching data", error);
            }
        });
        
        // Get the data
        this._dataService.data$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((data) =>
            {
                console.log("dv-bar ngOnInit");
                // Store the data
                this.data = data;
              
                this._prepareChartFileCount()
                this._prepareChartFileDistribution();
            });

        // Attach SVG fill fixer to all ApexCharts
        window['Apex'] = {
            chart: {
                events: {
                    mounted: (chart: any, options?: any): void =>
                    {
                        this._fixSvgFill(chart.el);
                    },
                    updated: (chart: any, options?: any): void =>
                    {
                        this._fixSvgFill(chart.el);
                    },
                },
            },
        };

       
    }
    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Fix the SVG fill references. This fix must be applied to all ApexCharts
     * charts in order to fix 'black color on gradient fills on certain browsers'
     * issue caused by the '<base>' tag.
     *
     * Fix based on https://gist.github.com/Kamshak/c84cdc175209d1a30f711abd6a81d472
     *
     * @param element
     * @private
     */
    private _fixSvgFill(element: Element): void
    {
        // Current URL
        const currentURL = this._router.url;

        // 1. Find all elements with 'fill' attribute within the element
        // 2. Filter out the ones that doesn't have cross reference so we only left with the ones that use the 'url(#id)' syntax
        // 3. Insert the 'currentURL' at the front of the 'fill' attribute value
        Array.from(element.querySelectorAll('*[fill]'))
            .filter(el => el.getAttribute('fill').indexOf('url(') !== -1)
            .forEach((el) =>
            {
                const attrVal = el.getAttribute('fill');
                el.setAttribute('fill', `url(${currentURL}${attrVal.slice(attrVal.indexOf('#'))}`);
            });
    }

    /**
     * Prepare the chart data from the data
     *
     * @private
     */
    private _prepareChartFileDistribution(): void {

        //Set the chart options
        this.chartFileDistribution = {
            chart      : {
                fontFamily: 'inherit',
                foreColor : 'inherit',
                height    : '100%',
                type      : 'polarArea',
                toolbar   : {
                    show: true,
                },
                zoom      : {
                    enabled: true,
                },
            },
            labels     : this.data.fileDistribution.labels,
            legend     : {
                position: 'bottom',
            },
            plotOptions: {
                polarArea: {
                    spokes: {
                        connectorColors: 'var(--fuse-border)',
                    },
                    rings : {
                        strokeColor: 'var(--fuse-border)',
                    },
                },
            },
            series     : this.data.fileDistribution.series,
            states     : {
                hover: {
                    filter: {
                        type : 'lighten',
                        value: 0.25,
                    },
                },
            },
            stroke     : {
                width: 2,
            },
            /*
            theme      : {
                monochrome: {
                    enabled       : true,
                    color         : '#93C5FD',
                    shadeIntensity: 0.75,
                    shadeTo       : 'dark',
                },
            },
            */
            theme: {
                palette: 'palette1' // upto palette10
              },
            tooltip    : {
                followCursor: true,
                theme       : 'dark',
            },
            yaxis      : {
                labels: {
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                    },
                },
            },
        };

    }
    
    private _prepareChartFileCount(): void {
        //Set the chart options
        this.chartFileCount = {
            chart      : {
                fontFamily: 'inherit',
                foreColor : 'inherit',
                height    : '100%',
                type      : 'donut',
                toolbar   : {
                    show: true,
                },
                zoom      : {
                    enabled: true,
                },
            },
            labels     : this.data.fileDistribution.labels,
            legend     : {
                position: 'bottom',
            },
            plotOptions: {
                pie: {
                    customScale: 0.9,
                    donut: {
                        size: '62.8318%',
                        labels: {
                            show: true,
                          }
                      }
                  }
            },
            series     : this.data.fileDistribution.series,
            states     : {
                hover: {
                    filter: {
                        type : 'lighten',
                        value: 0.25,
                    },
                },
            },
            stroke     : {
                width: 2,
            },
            /*
            theme      : {
                monochrome: {
                    enabled       : true,
                    color         : '#93C5FD',
                    shadeIntensity: 0.75,
                    shadeTo       : 'dark',
                },
            },
            */
            theme: {
                palette: 'palette1' // upto palette10
              },
            tooltip    : {
                followCursor: true,
                theme       : 'dark',
            },
            yaxis      : {
                labels: {
                    style: {
                        colors: 'var(--fuse-text-secondary)',
                    },
                },
            },
            responsive: [
                {
                  breakpoint: 480,
                  options: {
                  
                    legend: {
                      position: "right"
                    }
                  }
                }
              ]
        };

    }
}
